import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box, Select, Table, Thead, Tbody, Tr, Th, Td, Button, useToast, Flex, Text, Input,
  Menu, MenuButton, MenuList, MenuItem, IconButton,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  FormControl, FormLabel, Textarea, Tooltip,
} from "@chakra-ui/react";
import { FaPlus, FaEllipsisV } from "react-icons/fa";
import PageToolbar from "../../../components/shared/PageToolbar";
import { Panel } from "../../../components/shared/Section";
import { TableSkeleton } from "../../../components/shared/LoadingBlock";
import EditablePopover from "./components/EditablePopover";
import LogoCell from "./components/LogoCell";
import { PagesPopover, RetailersPopover, SuppliersPopover, BillingPopover, OrgsPopover } from "./components/GrantPopovers";
import OrganisationsView, { CreateOrgPopover } from "./OrganisationsView";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";
import { useAuthentication } from "../../../wrappers/UseAuthentication";
import { FaUserSecret } from "react-icons/fa";

const API = process.env.REACT_APP_API_URL;

// Display names for user_type values. The DB keeps the legacy 'SME' value —
// only the label changed to "Basic" (admin-v2 phase 4). 'CAM' arrived with
// TASK-user-creation-rework; 'SME' remains only for org-created viewers.
const USER_TYPE_LABELS = { admin: "Master", CAM: "CAM", org_admin: "Org Admin", supplier: "Supplier", SME: "Basic" };
const typeLabel = (t) => USER_TYPE_LABELS[t] || (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

// A field-only login: every page grant is a Field page (Scorecard = Curaden's scorecard; RepRoute = a rep's route).
const FIELD_PAGES = ["Scorecard", "RepRoute"];
const isFieldOnly = (row) => row.user_type !== "admin" && Array.isArray(row.page_names) && row.page_names.length > 0 && row.page_names.every((p) => FIELD_PAGES.includes(p));
const repKind = (row) => (row.page_names || []).map((p) => (p === "Scorecard" ? "Scorecard" : "My route")).join(" + ");

// The type-scoped views (spec: users.master-admins.md). There is deliberately
// no "all users" view — each type gets its own columns.
const VIEWS = [
  { key: "supplier", label: "Suppliers", type: "supplier" },
  { key: "CAM", label: "CAMs", type: "CAM" },
  // Reps (2026-10-07): field-only logins — every page they hold is a Field page (Scorecard / My route). Listed
  // here instead of under their user type (they are created as CAMs with one page).
  { key: "rep", label: "Reps", rep: true },
  { key: "organisations", label: "Organisations" },
  // Org admins were in no editable view until 2026-09-16: the master could
  // change their sign-in from the Organisations row but not their pages,
  // suppliers, sign-in toggle, invite or delete them.
  { key: "org_admin", label: "Org admins", type: "org_admin" },
  { key: "SME", label: "Org users (Basic)", type: "SME" },
  { key: "admin", label: "Masters", type: "admin" },
];

// State, not an action: reads at a glance down a column, still toggles on click.
const StateChip = ({ on, label, onClick, title }) => (
  <Tooltip label={title} openDelay={400}>
    <Button
      size="xs"
      variant="ghost"
      onClick={onClick}
      aria-pressed={on}
      minW="38px"
      px={1}
      fontWeight="600"
      color={on ? "signal.up" : "ink.400"}
      bg={on ? "signal.upSoft" : "ink.100"}
      _hover={{ bg: on ? "signal.upSoft" : "ink.200" }}
    >
      {label}
    </Button>
  </Tooltip>
);

const fmtShort = (d) => {
  if (!d) return "";
  const date = new Date(d);
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short" }).format(date);
};

const Users = () => {
  const { fetchSupplierAccess, refreshSupplierList, fetchOrganisations, organisations, isMaster, selectedOrg } = useSupplierRetailer();
  const { teleportIn } = useAuthentication();
  const navigate = useNavigate();
  const toast = useToast();
  const [teleporting, setTeleporting] = useState(null); // userid mid-teleport (button spinner)

  const [users, setUsers] = useState([]);
  const [typeOptions, setTypeOptions] = useState(["admin", "CAM", "org_admin", "supplier", "SME"]);
  const [suppliersList, setSuppliersList] = useState([]); // {suppliercode, org_code} for the org drawer
  const [pageSets, setPageSets] = useState({ pages: [], reportPages: [], camAdminPages: [], orgAdminPages: [], orgExtraPages: {} });
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [search, setSearch] = useState("");
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("usersView") || "supplier"; } catch (e) { return "supplier"; }
  });

  // Row-action modals (one shared instance each, driven by the ⋯ menu).
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailDraft, setEmailDraft] = useState({ subject: "", body: "" });
  const [typeTarget, setTypeTarget] = useState(null);
  const [typeDraft, setTypeDraft] = useState("");
  // Organisation for the new type (org admins / Basic users belong to one) — 2026-09-17.
  const [typeOrgDraft, setTypeOrgDraft] = useState("");

  const setViewPersist = (v) => {
    setView(v);
    try { localStorage.setItem("usersView", v); } catch (e) { /* ignore */ }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.post(`${API}/admin/get-users`, {});
      setUsers(response.data.users || []);
      if (Array.isArray(response.data.user_type)) setTypeOptions(response.data.user_type);
    } catch (error) {
      toast({ title: "Failed to load users", description: error?.response?.data?.message || "Technical error occurred", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchSuppliersAndPages = async () => {
    try {
      const response = await axios.post(`${API}/admin/get-suppliers-and-pages`, {});
      const d = response.data?.data || {};
      setSuppliersList(d.suppliers || []);
      setPageSets({
        pages: d.pages || [],
        reportPages: d.reportPages || [],
        camAdminPages: d.camAdminPages || [],
        orgAdminPages: d.orgAdminPages || [],
        orgExtraPages: d.orgExtraPages || {},
      });
    } catch (error) {
      /* popovers fall back to empty sets; the toast on get-users covers outages */
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchSuppliersAndPages();
    fetchSupplierAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeView = VIEWS.find((v) => v.key === view) || VIEWS[0];

  const rows = useMemo(() => {
    if (view === "organisations") return [];
    const q = search.trim().toLowerCase();
    return users
      .filter((row) => (activeView.rep ? isFieldOnly(row) : row.user_type === activeView.type && !isFieldOnly(row)))
      // Masters administer every org; the other views follow the org toggle
      // (rows without an org stay visible everywhere — pre-migration data).
      // A supplier row follows its supplier's MEMBERSHIP (home + extra orgs,
      // `supplier_org_codes` — 2026-09-17), so a supplier made visible in a
      // second organisation is listed under that toggle too.
      .filter((row) => {
        if (view === "admin" || !(isMaster && selectedOrg)) return true;
        const orgs = Array.isArray(row.supplier_org_codes) && row.supplier_org_codes.length ? row.supplier_org_codes : row.org_code ? [row.org_code] : [];
        return orgs.length === 0 || orgs.includes(selectedOrg);
      })
      .filter((row) => !q || `${row.fname || ""} ${row.username || ""} ${row.useremail || ""} ${row.owned_suppliercode || ""}`.toLowerCase().includes(q))
      .sort((a, b) => String(a.fname || "").localeCompare(String(b.fname || ""), undefined, { sensitivity: "base" }));
  }, [users, view, search, isMaster, selectedOrg, activeView.type]);

  // ── Row actions ────────────────────────────────────────────────────────────
  const simplePost = async (path, body, okTitle) => {
    try {
      const response = await axios.post(`${API}${path}`, body);
      if (okTitle) toast({ title: okTitle, description: response.data?.description || response.data?.message, status: "success", duration: 2500, isClosable: true });
      fetchUsers();
    } catch (error) {
      toast({ title: "Update failed", description: error?.response?.data?.description || error?.response?.data?.message || "Technical error", status: "error", duration: 5000, isClosable: true });
    }
  };

  const submitField = (userid, field, value) => {
    const routes = { fname: "/admin/upd-fname", username: "/admin/upd-username", useremail: "/admin/upd-useremail", userpass: "/admin/upd-userpass" };
    simplePost(routes[field], { userid, [field]: value }, "Updated");
  };

  const toggleFlag = (row, field) => {
    const routes = { activeyn: "/admin/upd-user-activeyn", restrictedyn: "/admin/upd-user-restrictedyn", subscriptionyn: "/admin/upd-user-subscriptionyn" };
    simplePost(routes[field], { userid: row.userid, [field]: !row[field] }, undefined);
  };

  const manualPay = (userid) => simplePost("/admin/ins-manual-payment", { userid }, "Manual payment recorded");

  const resendInvite = async (userid) => {
    try {
      const response = await axios.post(`${API}/admin/resend-invite`, { userid });
      toast({
        title: "Invite re-sent",
        description: response.data.message,
        status: response.data.data?.emailed === false ? "warning" : "success",
        duration: response.data.data?.emailed === false ? 15000 : 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({ title: "Failed to resend invite", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
    }
  };

  const deleteUser = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${row.fname} (${row.username})? ${row.user_type === "supplier" ? "This removes the supplier and every grant to it." : ""}`)) return;
    try {
      const response = await axios.post(`${API}/admin/del-user`, { userid: row.userid });
      toast({ title: response.data.title, description: response.data.description, status: "success", duration: 4000, isClosable: true });
    } catch (error) {
      toast({ title: "Failed to delete user", description: error?.response?.data?.description || error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      fetchUsers();
      fetchSupplierAccess();
      // Deleting a supplier revokes its grants code-wide — drop it from the
      // in-memory dropdown list too, without disturbing the current selection.
      if (row.user_type === "supplier") refreshSupplierList();
    }
  };

  const sendEmail = async () => {
    try {
      await axios.post(`${API}/admin/email-user`, { userid: emailTarget.userid, email: { subject: emailDraft.subject, body: emailDraft.body } });
      toast({ title: "Email sent", status: "success", duration: 3000, isClosable: true });
      setEmailTarget(null);
      setEmailDraft({ subject: "", body: "" });
    } catch (error) {
      toast({ title: "Failed to send email", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
    }
  };

  const typeNeedsOrg = typeDraft === "org_admin" || typeDraft === "SME";
  // Teleport in (2026-09-17): sign in as this account in place — no password,
  // no sign-out. The purple badge (TeleportBadge) brings the master back.
  const teleport = async (row) => {
    setTeleporting(row.userid);
    try {
      await teleportIn(row);
      toast({ title: `Teleported into ${row.fname || row.username}`, description: "You now see the app as they do. Use the purple badge (top right) to come back.", status: "info", duration: 5000, isClosable: true, colorScheme: "purple" });
    } catch (error) {
      toast({ title: "Teleport failed", description: error?.response?.data?.message || error?.message || "Technical error", status: "error", duration: 6000, isClosable: true });
      setTeleporting(null);
    }
  };

  const changeType = async () => {
    const body = { userid: typeTarget.userid, user_type: typeDraft };
    if (typeNeedsOrg && typeOrgDraft) body.org_code = typeOrgDraft;
    await simplePost("/admin/upd-user-type", body, `Type changed to ${typeLabel(typeDraft)}`);
    setTypeTarget(null);
  };

  // ── Cells ──────────────────────────────────────────────────────────────────
  const InviteCell = ({ row }) => (
    <Td px={1} py={1}>
      {row.invite_pending ? (
        <Flex align="center" gap={1}>
          <Text fontSize="xs" color="orange.500" fontWeight="600" whiteSpace="nowrap">Pending</Text>
          <Button size="xs" variant="ghost" onClick={() => resendInvite(row.userid)}>Resend</Button>
        </Flex>
      ) : (
        <Text fontSize="xs" color="ink.300">—</Text>
      )}
    </Td>
  );

  // billing=false hides the Sub chip — org admins are not billed accounts.
  const AccessCell = ({ row, billing = true }) => (
    <Td px={1} py={1}>
      <Flex gap={1}>
        <StateChip on={row.activeyn} label="Signin" title="Can sign in" onClick={() => toggleFlag(row, "activeyn")} />
        <StateChip on={row.restrictedyn} label="Restr" title="Restricted (report pages hidden for this account and, if it owns a supplier, for every CAM/org user viewing that supplier)" onClick={() => toggleFlag(row, "restrictedyn")} />
        {billing && <StateChip on={row.subscriptionyn} label="Sub" title="Payment subscription" onClick={() => toggleFlag(row, "subscriptionyn")} />}
      </Flex>
    </Td>
  );

  const BillingCell = ({ row }) => (
    <Td px={1} py={1}>
      {row.subscriptionyn ? (
        <Flex align="center" gap={1} whiteSpace="nowrap">
          <BillingPopover
            user={row}
            onSaved={fetchUsers}
            trigger={
              <Button size="xs" variant="ghost">
                R{((Number(row.subscription_amount) || 0) / 100).toLocaleString()}{row.date_last_paid ? ` · paid ${fmtShort(row.date_last_paid)}` : ""}
              </Button>
            }
          />
          <Button size="xs" variant="secondary" onClick={() => manualPay(row.userid)}>Pay</Button>
        </Flex>
      ) : (
        <Text fontSize="xs" color="ink.300">—</Text>
      )}
    </Td>
  );

  // retailers=false for org admins: their retailer access IS the organisation's
  // chips (Organisations view), never a per-user row.
  const GrantsCell = ({ row, retailers = true }) => (
    <Td px={1} py={1}>
      <Flex gap={0}>
        <PagesPopover user={row} pageSets={pageSets} onSaved={fetchUsers} />
        {retailers && <RetailersPopover user={row} onSaved={fetchUsers} />}
        <SuppliersPopover user={row} onSaved={fetchUsers} />
      </Flex>
    </Td>
  );

  // Masters only (the page is theirs; the API gate is requireMaster). Never
  // offered on a master row or a switched-off account.
  const TeleportCell = ({ row }) => (
    <Td px={1} py={1}>
      {isMaster && row.user_type !== "admin" && (
        <Tooltip label={row.activeyn ? `Sign in as ${row.fname || row.username} without their password. A purple badge brings you back.` : "Switched off (Signin) — nothing to see as them"} openDelay={400}>
          <Button
            size="xs"
            colorScheme="purple"
            variant="solid"
            leftIcon={<FaUserSecret />}
            onClick={() => teleport(row)}
            isDisabled={!row.activeyn}
            isLoading={teleporting === row.userid}
            loadingText="Going"
            aria-label={`Teleport in as ${row.username}`}
          >
            Teleport in
          </Button>
        </Tooltip>
      )}
    </Td>
  );

  const RowMenu = ({ row }) => (
    <Td px={1} py={1}>
      <Menu isLazy>
        <MenuButton as={IconButton} icon={<FaEllipsisV />} size="xs" variant="ghost" aria-label="More actions" />
        <MenuList fontSize="sm">
          <MenuItem onClick={() => { setTypeTarget(row); setTypeDraft(row.user_type); setTypeOrgDraft(row.org_code || selectedOrg || "SPEARMGMT"); }}>Change type…</MenuItem>
          <MenuItem onClick={() => { setEmailTarget(row); setEmailDraft({ subject: "", body: "" }); }}>Send email…</MenuItem>
          <MenuItem color="signal.down" onClick={() => deleteUser(row)}>Delete user</MenuItem>
        </MenuList>
      </Menu>
    </Td>
  );

  // "Teleport in" is a purple column on every non-master view (2026-09-17).
  const TELEPORT_HEADER = <Text as="span" color="purple.600">Teleport in</Text>;
  const headers = {
    supplier: ["Logo", "Code", "Org", "Name", "Username", "Password", "Email", "Invite", "Access", "Billing", "Grants", TELEPORT_HEADER, ""],
    CAM: ["Logo", "Name", "Username", "Password", "Email", "Invite", "Access", "Billing", "Grants", TELEPORT_HEADER, ""],
    SME: ["Name", "Org", "Username", "Password", "Email", "Invite", "Access", "Grants", TELEPORT_HEADER, ""],
    org_admin: ["Name", "Org", "Username", "Password", "Email", "Invite", "Access", "Grants", TELEPORT_HEADER, ""],
    admin: ["Name", "Username", "Password", "Email"],
    rep: ["Name", "Org", "Rep for", "Username", "Password", "Access", TELEPORT_HEADER, ""],
  };

  const renderRow = (row) => {
    const fields = {
      fname: <EditablePopover key="f" label="Name" value={row.fname} fieldName="fname" onSubmit={(f, v) => submitField(row.userid, "fname", v)} />,
      username: <EditablePopover key="u" label="Username" value={row.username} fieldName="username" onSubmit={(f, v) => submitField(row.userid, "username", v)} />,
      userpass: <EditablePopover key="p" label="Password" value={row.userpass} fieldName="userpass" onSubmit={(f, v) => submitField(row.userid, "userpass", v)} />,
      useremail: <EditablePopover key="e" label="Email" value={row.useremail} fieldName="useremail" maxW="200px" onSubmit={(f, v) => submitField(row.userid, "useremail", v)} />,
    };
    if (view === "supplier") {
      return (
        <Tr key={row.userid}>
          <Td px={1} py={1}><LogoCell storedCode={row.owned_suppliercode || row.fname} label={row.fname} /></Td>
          <Td px={1} py={1} fontWeight="600" whiteSpace="nowrap">{row.owned_suppliercode || <Text as="span" fontSize="xs" color="signal.down">no supplier row</Text>}</Td>
          {/* Home organisation (+N extra) — move / share the supplier from here (2026-09-17). */}
          <Td px={1} py={1} whiteSpace="nowrap">
            <OrgsPopover
              user={row}
              organisations={organisations}
              onSaved={() => {
                fetchUsers();
                fetchSuppliersAndPages();
                refreshSupplierList();
                fetchOrganisations();
              }}
            />
          </Td>
          {fields.fname}
          {fields.username}
          {fields.userpass}
          {fields.useremail}
          <InviteCell row={row} />
          <AccessCell row={row} />
          <BillingCell row={row} />
          <GrantsCell row={row} />
          <TeleportCell row={row} />
          <RowMenu row={row} />
        </Tr>
      );
    }
    if (view === "CAM") {
      return (
        <Tr key={row.userid}>
          <Td px={1} py={1}><LogoCell storedCode={`CAM_${row.userid}`} label={row.fname} /></Td>
          {fields.fname}
          {fields.username}
          {fields.userpass}
          {fields.useremail}
          <InviteCell row={row} />
          <AccessCell row={row} />
          <BillingCell row={row} />
          <GrantsCell row={row} />
          <TeleportCell row={row} />
          <RowMenu row={row} />
        </Tr>
      );
    }
    if (view === "SME") {
      return (
        <Tr key={row.userid}>
          {fields.fname}
          <Td px={1} py={1}>{row.org_code || "—"}</Td>
          {fields.username}
          {fields.userpass}
          {fields.useremail}
          <InviteCell row={row} />
          <AccessCell row={row} />
          <GrantsCell row={row} />
          <TeleportCell row={row} />
          <RowMenu row={row} />
        </Tr>
      );
    }
    if (view === "rep") {
      return (
        <Tr key={row.userid}>
          {fields.fname}
          <Td px={1} py={1} whiteSpace="nowrap">{row.org_code || "—"}</Td>
          <Td px={1} py={1} whiteSpace="nowrap">{repKind(row)}</Td>
          {fields.username}
          {fields.userpass}
          <AccessCell row={row} billing={false} />
          <TeleportCell row={row} />
          <RowMenu row={row} />
        </Tr>
      );
    }
    if (view === "org_admin") {
      // Retailers come from the org's chips (Organisations view), so no
      // Retailers popover; suppliers are capped to the org server-side.
      return (
        <Tr key={row.userid}>
          {fields.fname}
          <Td px={1} py={1} whiteSpace="nowrap">{row.org_code || "—"}</Td>
          {fields.username}
          {fields.userpass}
          {fields.useremail}
          <InviteCell row={row} />
          <AccessCell row={row} billing={false} />
          <GrantsCell row={row} retailers={false} />
          <TeleportCell row={row} />
          <RowMenu row={row} />
        </Tr>
      );
    }
    // masters: self-service fields only — no toggles, no delete.
    return (
      <Tr key={row.userid}>
        {fields.fname}
        {fields.username}
        {fields.userpass}
        {fields.useremail}
      </Tr>
    );
  };

  // Add Rep (2026-10-07): a Curaden scorecard rep = a CAM with ONLY the Scorecard page, supplier Curaden
  // (PRIME DENTAL), Dis-Chem — the same shape as the CURADEN login. Blue Mango route reps follow with the rep tracker.
  const [repDraft, setRepDraft] = useState(null);
  const [repSaving, setRepSaving] = useState(false);
  const saveRep = async () => {
    const d = repDraft;
    if (!d.fname.trim() || !d.username.trim() || !d.password.trim()) {
      toast({ title: "Name, username and password are needed", status: "warning", isClosable: true });
      return;
    }
    setRepSaving(true);
    try {
      await axios.post(`${API}/admin/ins-user`, {
        user_type: "CAM", fname: d.fname.trim(), username: d.username.trim(), password: d.password, invite: false,
        suppliers: ["PRIME DENTAL"], pages: ["Scorecard"], default_supplier: "PRIME DENTAL", default_retailer: "DIS",
        retailerAccess: { pnp: false, chk: false, dis: true, mak: false, cli: false, med: false, pet: false },
        org_code: "SPEARMGMT",
      });
      toast({ title: "Rep added", description: `${d.fname.trim()} · ${d.username.trim()} — Scorecard only`, status: "success", isClosable: true });
      setRepDraft(null);
      fetchUsers();
    } catch (error) {
      toast({ title: "Not added", description: error?.response?.data?.message || error?.response?.data?.description || "Technical error", status: "error", isClosable: true });
    } finally {
      setRepSaving(false);
    }
  };

  const addActions = () => {
    if (view === "supplier") return <Button leftIcon={<FaPlus />} onClick={() => navigate("/addUser?type=supplier")}>Add Supplier</Button>;
    if (view === "CAM") return <Button leftIcon={<FaPlus />} onClick={() => navigate("/addUser?type=CAM")}>Add CAM</Button>;
    if (view === "organisations") {
      return (
        <>
          <Button variant="subtle" leftIcon={<FaPlus />} onClick={() => navigate("/addUser?type=org_admin")}>Add Org Admin</Button>
          <CreateOrgPopover onCreated={fetchUsers} />
        </>
      );
    }
    if (view === "org_admin") return <Button leftIcon={<FaPlus />} onClick={() => navigate("/addUser?type=org_admin")}>Add Org Admin</Button>;
    if (view === "SME") return <Text fontSize="xs" color="ink.500">Org users are created by their org admins (invites)</Text>;
    if (view === "rep") return <Button leftIcon={<FaPlus />} onClick={() => setRepDraft({ kind: "curaden", fname: "", username: "", password: "" })}>Add Rep</Button>;
    return null;
  };

  return (
    <Box>
      <PageToolbar
        filters={
          <>
            <Select aria-label="User type view" value={view} onChange={(e) => setViewPersist(e.target.value)} width="190px">
              {VIEWS.map((v) => (
                <option key={v.key} value={v.key}>{v.label}</option>
              ))}
            </Select>
            <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} width="220px" />
            {view !== "organisations" && (
              <Text fontSize="sm" color="ink.500" whiteSpace="nowrap">
                {rows.length} {rows.length === 1 ? "user" : "users"}
              </Text>
            )}
          </>
        }
        actions={addActions()}
      />

      <Modal isOpen={!!repDraft} onClose={() => setRepDraft(null)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add rep</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={1}>Rep for</Text>
            <Select mb={3} value={repDraft?.kind || "curaden"} onChange={(e) => setRepDraft({ ...repDraft, kind: e.target.value })}>
              <option value="curaden">Curaden — Dis-Chem Scorecard</option>
              <option value="bluemango" disabled>Blue Mango — My route (when the rep tracker goes live)</option>
            </Select>
            <Text fontSize="sm" mb={1}>Name</Text>
            <Input mb={3} value={repDraft?.fname || ""} onChange={(e) => setRepDraft({ ...repDraft, fname: e.target.value })} placeholder="e.g. Janet Mokoena" />
            <Text fontSize="sm" mb={1}>Username</Text>
            <Input mb={3} value={repDraft?.username || ""} onChange={(e) => setRepDraft({ ...repDraft, username: e.target.value })} />
            <Text fontSize="sm" mb={1}>Password</Text>
            <Input mb={2} value={repDraft?.password || ""} onChange={(e) => setRepDraft({ ...repDraft, password: e.target.value })} />
            <Text fontSize="xs" color="ink.500">The rep sees only the Curaden Scorecard (Dis-Chem) — nothing else.</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setRepDraft(null)}>Cancel</Button>
            <Button colorScheme="teal" onClick={saveRep} isLoading={repSaving}>Add rep</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {view === "organisations" ? (
        <OrganisationsView
          allUsers={users}
          allSuppliers={suppliersList}
          search={search}
          onManageAdmins={() => setViewPersist("org_admin")}
          onChanged={() => {
            fetchUsers();
            fetchSuppliersAndPages();
          }}
        />
      ) : (
        <Panel padded={false} maxH="calc(100vh - 240px)" overflow="auto">
          {loadingUsers ? (
            <TableSkeleton rows={12} minH="420px" />
          ) : (
            <Table variant="striped" size="sm" colorScheme="custom" textStyle="smallText">
              <Thead position="sticky" top={0} zIndex={1} bg="white">
                <Tr>
                  {headers[view].map((h, i) => (
                    <Th key={`${h}-${i}`} px={1} py={1} bg="gray.50">{h}</Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>{rows.map(renderRow)}</Tbody>
            </Table>
          )}
        </Panel>
      )}

      {/* Send email modal (shared instance, driven from the ⋯ menu) */}
      <Modal isOpen={!!emailTarget} onClose={() => setEmailTarget(null)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Email {emailTarget?.fname}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={2}>
              <FormLabel fontSize="sm">Subject</FormLabel>
              <Input size="sm" value={emailDraft.subject} onChange={(e) => setEmailDraft({ ...emailDraft, subject: e.target.value })} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Body</FormLabel>
              <Textarea size="sm" rows={6} value={emailDraft.body} onChange={(e) => setEmailDraft({ ...emailDraft, body: e.target.value })} />
            </FormControl>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setEmailTarget(null)}>Cancel</Button>
            <Button size="sm" colorScheme="teal" onClick={sendEmail} isDisabled={!emailDraft.subject && !emailDraft.body}>Send</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Change type modal */}
      <Modal isOpen={!!typeTarget} onClose={() => setTypeTarget(null)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Change type — {typeTarget?.fname}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Select value={typeDraft} onChange={(e) => setTypeDraft(e.target.value)}>
              {typeOptions.map((t) => (
                <option key={t} value={t}>{typeLabel(t)}</option>
              ))}
            </Select>
            {typeNeedsOrg && (
              <FormControl mt={3}>
                <FormLabel fontSize="sm">Organisation</FormLabel>
                <Select value={typeOrgDraft} onChange={(e) => setTypeOrgDraft(e.target.value)} aria-label="Organisation for the new type">
                  {(organisations.length ? organisations : [{ org_code: typeOrgDraft, org_name: typeOrgDraft }]).map((o) => (
                    <option key={o.org_code} value={o.org_code}>{o.org_name} ({o.org_code})</option>
                  ))}
                </Select>
                {typeDraft === "org_admin" && (
                  <Text fontSize="xs" color="ink.500" mt={1}>
                    Pages are capped to the org-admin set; retailers become the organisation's chips. Supplier grants are kept — make those
                    suppliers visible in the organisation (Suppliers view → Org) so its admin screens can see them.
                  </Text>
                )}
              </FormControl>
            )}
            <Text fontSize="xs" color="ink.500" mt={2}>
              The user moves to the matching view. They must sign in again for the change to fully apply.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setTypeTarget(null)}>Cancel</Button>
            <Button size="sm" colorScheme="teal" onClick={changeType} isDisabled={!typeDraft || typeDraft === typeTarget?.user_type}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Users;
